if (!customElements.get('klaviyo-custom-bis')) {
  customElements.define('klaviyo-custom-bis', class KlaviyoCustomBis extends HTMLElement {
    constructor() {
      super();

      this.revision = '2024-06-15';
      this.sectionId = this.dataset.sectionId;
      this.publicKey = this.dataset.publicKey;
      this.listId = this.dataset.listId;
      this.variantData = this.getVariantData();
      this.modal = this.querySelector('modal-dialog');
      this.form = this.querySelector('[data-klaviyo-custom-bis-form]');
      this.message = this.querySelector('[data-klaviyo-custom-bis-message]');
      this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;
      this.activeVariantId = null;
    }

    connectedCallback() {
      this.updateControls(this.getCurrentVariantId());

      document.addEventListener('variant:change', (event) => {
        if (!event.detail || !event.detail.variant) return;
        this.updateControls(event.detail.variant.id);
      });

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-klaviyo-custom-bis-trigger]');
        if (!trigger) return;
        if (trigger.dataset.sectionId !== this.sectionId) return;

        event.preventDefault();
        this.open(trigger);
      });

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }
    }

    getVariantData() {
      const script = this.querySelector('[data-klaviyo-custom-bis-variants]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    getCurrentVariantId() {
      const input = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
      return input ? input.value : null;
    }

    findVariant(variantId) {
      return this.variantData.find((variant) => variant.id.toString() === variantId.toString());
    }

    getAvailabilityState(variantId) {
      const variant = this.findVariant(variantId);
      if (!variant) return 'unavailable';

      const threshold = Number(this.dataset.threshold || 0);
      if (variant.available && Number.isFinite(threshold) && threshold > 0 && variant.inventory_quantity < threshold) {
        return 'custom-bis';
      }

      if (variant.available) return 'available';
      return 'legacy-bis';
    }

    updateControls(variantId) {
      if (!variantId) return;

      this.activeVariantId = variantId;
      const state = this.getAvailabilityState(variantId);
      const controls = document.querySelectorAll(`[data-klaviyo-custom-bis-section="${this.sectionId}"]`);

      controls.forEach((control) => {
        const controlType = control.dataset.klaviyoCustomBisControl;
        const show = controlType === state;
        control.toggleAttribute('hidden', !show);

        if (controlType === 'available') {
          control.disabled = !show;
        }

        if (show && control.hasAttribute('data-variant-id')) {
          control.dataset.variantId = variantId;
        }
      });

      document.querySelectorAll(`#product-form-${this.sectionId} .shopify-payment-button, #product-form-${this.sectionId}--alt .shopify-payment-button`).forEach((button) => {
        button.toggleAttribute('hidden', state !== 'available');
      });
    }

    open(trigger) {
      this.activeVariantId = trigger.dataset.variantId || this.getCurrentVariantId();
      this.resetMessage();

      if (this.form) {
        this.form.reset();
      }

      if (this.modal && typeof this.modal.show === 'function') {
        this.modal.show(trigger);
      }
    }

    resetMessage() {
      if (!this.message) return;
      this.message.textContent = '';
      this.message.classList.remove('errors');
      this.message.hidden = true;
    }

    setMessage(message, isError = false) {
      if (!this.message) return;
      this.message.textContent = message;
      this.message.classList.toggle('errors', isError);
      this.message.hidden = false;
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('loading', isLoading);
    }

    async onSubmit(event) {
      event.preventDefault();
      this.resetMessage();

      const formData = new FormData(this.form);
      const email = formData.get('email');
      const subscribeToNewsletter = formData.get('newsletter') === 'on';

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.setLoading(true);

      try {
        await this.createBackInStockSubscription(email, this.activeVariantId);

        if (subscribeToNewsletter && this.listId) {
          try {
            await this.createMarketingSubscription(email);
            this.setMessage(this.dataset.newsletterSuccessMessage || this.dataset.successMessage);
          } catch (error) {
            console.error(error);
            this.setMessage(this.dataset.newsletterErrorMessage || this.dataset.successMessage, true);
          }
        } else {
          this.setMessage(this.dataset.successMessage);
        }
      } catch (error) {
        console.error(error);
        this.setMessage(this.dataset.errorMessage, true);
      } finally {
        this.setLoading(false);
      }
    }

    async createBackInStockSubscription(email, variantId) {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      };

      return this.postToKlaviyo('back-in-stock-subscriptions', payload);
    }

    async createMarketingSubscription(email) {
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED'
                      }
                    }
                  }
                }
              }
            },
            custom_source: 'Custom back in stock modal'
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.listId
              }
            }
          }
        }
      };

      return this.postToKlaviyo('subscriptions', payload);
    }

    async postToKlaviyo(endpoint, payload) {
      const response = await fetch(`https://a.klaviyo.com/client/${endpoint}/?company_id=${encodeURIComponent(this.publicKey)}`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          revision: this.revision
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Klaviyo request failed with ${response.status}`);
      }

      return response;
    }
  });
}
